import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const CAT = { policy:'Policy', guide:'User Guide', other:'Other' };
const AUD = { all:'Everyone', staff:'Teaching Staff', it:'IT only' };
const statusBadge = s => {
  const map = { indexed:'badge-completed', indexing:'badge-pending', pending:'badge-pending', error:'badge-faulty' };
  const label = { indexed:'✓ AI-ready', indexing:'Indexing…', pending:'Queued', error:'Index error' };
  return <span className={`badge ${map[s]||'badge-normal'}`}>{label[s]||s}</span>;
};
const fmtSize = b => !b ? '—' : b>1e6 ? (b/1e6).toFixed(1)+' MB' : (b/1e3).toFixed(0)+' KB';

export default function Documents() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ title:'', category:'policy', audience:'all', campus_id:'', file:null });
  const [art, setArt] = useState(null);   // article editor: null | {…fields}
  const [revs, setRevs] = useState(null); // history viewer: null | {title, list:[]}
  const [topics, setTopics] = useState({}); // { [docId]: [heading, …] }
  const [openTopics, setOpenTopics] = useState(null); // docId currently expanded
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => { api.get('/documents').then(r => setRows(r.data)).catch(()=>{}); }, []);
  useEffect(() => { load(); api.get('/master/campuses').then(r => setCampuses(r.data.filter(c=>c.active))).catch(()=>{}); }, [load]);
  // Poll while anything is still indexing
  useEffect(() => {
    if (!rows.some(r => r.status==='indexing' || r.status==='pending')) return;
    const t = setInterval(load, 4000); return () => clearInterval(t);
  }, [rows, load]);

  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;

  const openNew = () => { setForm({ title:'', category:'policy', audience:'all', campus_id:'', file:null }); setErr(''); setModal(true); };
  const onFile = e => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 20*1024*1024) { setErr('File too large (max 20 MB)'); return; }
    setForm(s => ({ ...s, file:f, title: s.title || f.name.replace(/\.[^.]+$/,'') }));
    setErr('');
  };
  const save = async () => {
    if (!form.title.trim()) { setErr('Enter a title'); return; }
    if (!form.file) { setErr('Choose a file (PDF, DOCX, TXT, MD)'); return; }
    setBusy(true); setErr('');
    try {
      const data = await new Promise((res,rej) => { const r=new FileReader(); r.onload=()=>res(r.result); r.onerror=rej; r.readAsDataURL(form.file); });
      await api.post('/documents', {
        title: form.title.trim(), category: form.category, audience: form.audience,
        campus_id: form.campus_id ? parseInt(form.campus_id) : null,
        orig_name: form.file.name, mime: form.file.type, data,
      });
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error || 'Upload failed'); } finally { setBusy(false); }
  };
  const del = async r => { if (!window.confirm(`Delete "${r.title}"?`)) return; await api.delete(`/documents/${r.id}`); load(); };
  const reindex = async r => { await api.post(`/documents/${r.id}/reindex`); load(); };
  const toggleAi = async r => { await api.put(`/documents/${r.id}`, { use_for_ai: !r.use_for_ai }); load(); };

  const newArticle = () => { setArt({ title:'', category:'guide', audience:'all', campus_id:'', applies_to:'', summary:'', body:'', tags:'' }); setErr(''); };
  const editArticle = async r => {
    try { const d = (await api.get(`/documents/${r.id}/edit`)).data;
      setArt({ id:d.id, title:d.title||'', category:d.category||'guide', audience:d.audience||'all', campus_id:d.campus_id?String(d.campus_id):'',
        applies_to:d.applies_to||'', summary:d.summary||'', body:d.body||'', tags:d.tags||'' }); setErr(''); } catch(e){}
  };
  const saveArticle = async () => {
    if (!art.title.trim()) { setErr('Enter a title'); return; }
    if (!art.body.trim())  { setErr('Enter the content / steps'); return; }
    setBusy(true); setErr('');
    const body = { title:art.title.trim(), category:art.category, audience:art.audience,
      campus_id: art.campus_id ? parseInt(art.campus_id) : null, applies_to:art.applies_to, summary:art.summary, body:art.body, tags:art.tags, note:art.note };
    try {
      if (art.id) await api.put(`/documents/article/${art.id}`, body);
      else await api.post('/documents/article', body);
      setArt(null); load();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  const importGuides = async () => {
    if (!window.confirm('Import the built-in IT guides & policy?\nExisting imported ones are refreshed (your edits to them will be overwritten).')) return;
    setBusy(true);
    try { const r = await api.post('/documents/import-guides');
      alert(`Imported ${r.data.total} documents (${r.data.created} new, ${r.data.updated} refreshed). They will finish indexing for the AI shortly.`);
      load();
    } catch(e) { alert(e.response?.data?.error || 'Import failed'); } finally { setBusy(false); }
  };
  const showHistory = async r => {
    try { const list = (await api.get(`/documents/${r.id}/revisions`)).data;
      setRevs({ title:r.title, list }); } catch(e) { alert('Could not load history'); }
  };
  // Toggle the "topics covered" outline for an article (the ## / numbered headings in its body)
  const toggleTopics = async r => {
    if (openTopics === r.id) { setOpenTopics(null); return; }
    setOpenTopics(r.id);
    if (topics[r.id]) return;
    try {
      const d = (await api.get(`/documents/${r.id}/edit`)).data;
      const heads = (d.body || '').split('\n')
        .map(l => l.trim())
        .filter(l => /^#{1,4}\s+/.test(l) || /^\d+\.\s+/.test(l) || /^\*\*[^*]+\*\*$/.test(l))
        .map(l => l.replace(/^#{1,4}\s+/, '').replace(/^\d+\.\s+/, '').replace(/\*\*/g, '').replace(/:$/, ''));
      setTopics(s => ({ ...s, [r.id]: heads }));
    } catch(e) { setTopics(s => ({ ...s, [r.id]: [] })); }
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Documents — Policies &amp; User Guides</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Upload documents for staff to read. Indexed ones are used by the AI assistant to answer questions.</p>
        </div>
        <div className="flex-gap" style={{ gap:8 }}>
          <button className="btn btn-primary" onClick={newArticle}>+ New Article</button>
          <button className="btn btn-secondary" onClick={openNew}>⬆ Upload File</button>
          <button className="btn btn-secondary" onClick={importGuides} disabled={busy} title="Load the built-in IT guides & policy as editable articles">📥 Import built-in guides</button>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No documents yet. Upload your first policy or guide.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Title</th><th>Type</th><th>Audience</th><th>Campus</th><th>Size</th><th>AI Status</th><th>Use in AI</th><th></th></tr></thead>
            <tbody>{rows.map(r => (
              <React.Fragment key={r.id}>
              <tr>
                <td><div style={{ fontWeight:500 }}>{r.title}</div><div style={{ fontSize:11, color:'var(--text2)' }}>{r.source==='article'?'📝 In-portal article':r.orig_name}{r.chunk_count?` · ${r.chunk_count} passages`:''}</div>
                  {r.status==='error' && <div style={{ fontSize:11, color:'var(--red)' }}>Could not index — try a PDF/DOCX/TXT.</div>}</td>
                <td style={{ fontSize:12 }}>{CAT[r.category]||r.category}</td>
                <td style={{ fontSize:12 }}>{AUD[r.audience]||r.audience}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{r.campus_name || 'All'}</td>
                <td style={{ fontSize:12 }}>{fmtSize(r.size_bytes)}</td>
                <td>{statusBadge(r.status)}</td>
                <td><label className="opt" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12 }}>
                  <input type="checkbox" checked={!!r.use_for_ai} onChange={() => toggleAi(r)} /> </label></td>
                <td><div className="flex-gap" style={{ gap:6 }}>
                  <a className="btn btn-secondary btn-sm" href={`/api/documents/${r.id}/file`} target="_blank" rel="noopener noreferrer">View</a>
                  {r.source === 'article' && <button className="btn btn-secondary btn-sm" onClick={() => toggleTopics(r)}>{openTopics===r.id?'Hide topics':'Topics'}</button>}
                  {r.source === 'article' && <button className="btn btn-secondary btn-sm" onClick={() => editArticle(r)}>Edit</button>}
                  {r.source === 'article' && <button className="btn btn-secondary btn-sm" onClick={() => showHistory(r)}>History</button>}
                  <button className="btn btn-secondary btn-sm" onClick={() => reindex(r)}>Re-index</button>
                  <button className="btn btn-danger btn-sm" onClick={() => del(r)}>Delete</button>
                </div></td>
              </tr>
              {openTopics===r.id && (
                <tr><td colSpan={8} style={{ background:'var(--bg2,#f6f8fc)', padding:'10px 14px' }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>Topics covered in “{r.title}”</div>
                  {!topics[r.id] ? <div style={{ fontSize:12, color:'var(--text2)' }}>Loading…</div>
                    : !topics[r.id].length ? <div style={{ fontSize:12, color:'var(--text2)' }}>No sub-topics detected. Use <b>##</b> headings or numbered steps in the content to structure this document.</div>
                    : <ol style={{ margin:0, paddingLeft:20, columns:'2', fontSize:13, lineHeight:1.7 }}>
                        {topics[r.id].map((t,i) => <li key={i}>{t}</li>)}
                      </ol>}
                  <button className="btn btn-secondary btn-sm" style={{ marginTop:8 }} onClick={() => editArticle(r)}>Update this document</button>
                </td></tr>
              )}
              </React.Fragment>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:480 }}>
            <div className="modal-header"><span className="modal-title">Upload Document</span><button className="modal-close" onClick={() => setModal(false)}>×</button></div>
            <div className="form-group"><label>Title *</label><input value={form.title} onChange={e => setForm(s=>({...s,title:e.target.value}))} placeholder="e.g. Acceptable Use Policy" /></div>
            <div className="form-row">
              <div className="form-group"><label>Type</label>
                <select value={form.category} onChange={e => setForm(s=>({...s,category:e.target.value, campus_id: e.target.value==='policy'?'':s.campus_id}))}>
                  <option value="policy">Policy</option><option value="guide">User Guide</option><option value="other">Other</option>
                </select></div>
              <div className="form-group"><label>Audience</label>
                <select value={form.audience} onChange={e => setForm(s=>({...s,audience:e.target.value}))}>
                  <option value="all">Everyone</option><option value="staff">Teaching Staff</option><option value="it">IT only</option>
                </select></div>
            </div>
            {form.category === 'policy'
              ? <div className="form-group"><label>Campus</label><input value="All campuses" disabled style={{ color:'var(--text2)' }} />
                  <span style={{ fontSize:11, color:'var(--text2)' }}>Policies apply to every campus.</span></div>
              : <div className="form-group"><label>Campus (blank = all)</label>
                  <select value={form.campus_id} onChange={e => setForm(s=>({...s,campus_id:e.target.value}))}>
                    <option value="">All campuses</option>{campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select></div>}
            <div className="form-group"><label>File * <span style={{ fontSize:11, color:'var(--text2)' }}>(PDF, DOCX, TXT, MD — max 20 MB)</span></label>
              <input type="file" accept=".pdf,.docx,.txt,.md,.csv" onChange={onFile} /></div>
            <div style={{ fontSize:11, color:'var(--text2)', marginBottom:8 }}>After upload the document is indexed for the AI in the background (status shows “Indexing…” then “AI-ready”).</div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Uploading…':'Upload'}</button>
            </div>
          </div>
        </div>
      )}

      {art && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setArt(null)}>
          <div className="modal" style={{ maxWidth:640, maxHeight:'90vh', overflowY:'auto' }}>
            <div className="modal-header"><span className="modal-title">{art.id?'Edit Article':'New Article'}</span><button className="modal-close" onClick={() => setArt(null)}>×</button></div>
            <div style={{ fontSize:11, color:'var(--text2)', marginBottom:12 }}>Write in a clear, fixed structure so the AI answers reliably. Each article is re-indexed for the AI on save.</div>
            <div className="form-group"><label>Title *</label><input value={art.title} onChange={e => setArt(s=>({...s,title:e.target.value}))} placeholder="e.g. Projector shows 'No Signal'" /></div>
            <div className="form-row">
              <div className="form-group"><label>Type</label>
                <select value={art.category} onChange={e => setArt(s=>({...s,category:e.target.value, campus_id: e.target.value==='policy'?'':s.campus_id}))}>
                  <option value="guide">User Guide</option><option value="policy">Policy</option><option value="other">Other</option>
                </select></div>
              <div className="form-group"><label>Audience</label>
                <select value={art.audience} onChange={e => setArt(s=>({...s,audience:e.target.value}))}>
                  <option value="all">Everyone</option><option value="staff">Teaching Staff</option><option value="it">IT only</option>
                </select></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Applies to <span style={{ fontSize:11, color:'var(--text2)' }}>(device / system)</span></label>
                <input value={art.applies_to} onChange={e => setArt(s=>({...s,applies_to:e.target.value}))} placeholder="e.g. Classroom projector, Smart Panel" /></div>
              {art.category === 'policy'
                ? <div className="form-group"><label>Campus</label>
                    <input value="All campuses" disabled style={{ color:'var(--text2)' }} />
                    <span style={{ fontSize:11, color:'var(--text2)' }}>Policies apply to every campus.</span></div>
                : <div className="form-group"><label>Campus (blank = all)</label>
                    <select value={art.campus_id} onChange={e => setArt(s=>({...s,campus_id:e.target.value}))}>
                      <option value="">All campuses</option>{campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select></div>}
            </div>
            <div className="form-group"><label>Summary <span style={{ fontSize:11, color:'var(--text2)' }}>(one line — the problem or purpose)</span></label>
              <input value={art.summary} onChange={e => setArt(s=>({...s,summary:e.target.value}))} placeholder="e.g. Fixing a projector that shows No Signal" /></div>
            <div className="form-group"><label>Content / Steps *</label>
              <textarea rows={9} value={art.body} onChange={e => setArt(s=>({...s,body:e.target.value}))}
                placeholder={"Write clear steps, one per line, e.g.\n1. Check the projector is powered on.\n2. Press Source/Input and select HDMI 1.\n3. Reseat the HDMI cable at both ends.\n4. If still no signal, raise a ticket."} /></div>
            <div className="form-group"><label>Keywords <span style={{ fontSize:11, color:'var(--text2)' }}>(comma-separated — helps the AI find it)</span></label>
              <input value={art.tags} onChange={e => setArt(s=>({...s,tags:e.target.value}))} placeholder="projector, no signal, hdmi, display" /></div>
            {art.id && <>
              <div style={{ fontSize:11, color:'var(--text2)', marginBottom:8, background:'var(--bg2,#f6f8fc)', padding:'8px 10px', borderRadius:6 }}>
                The current procedure is shown above. <b>Edit any line</b> to correct it, or <b>type new steps at the end</b> to add to it. Saving records this change in the article's History.
              </div>
              <div className="form-group"><label>Change note <span style={{ fontSize:11, color:'var(--text2)' }}>(optional — what did you change?)</span></label>
                <input value={art.note||''} onChange={e => setArt(s=>({...s,note:e.target.value}))} placeholder="e.g. Added step for HDMI 2 input" /></div>
            </>}
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setArt(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveArticle} disabled={busy}>{busy?'Saving…':(art.id?'Save & Re-index':'Create & Index')}</button>
            </div>
          </div>
        </div>
      )}

      {revs && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setRevs(null)}>
          <div className="modal" style={{ maxWidth:620, maxHeight:'90vh', overflowY:'auto' }}>
            <div className="modal-header"><span className="modal-title">Edit history — {revs.title}</span><button className="modal-close" onClick={() => setRevs(null)}>×</button></div>
            {!revs.list.length ? <div style={{ fontSize:13, color:'var(--text2)', padding:'8px 0' }}>No changes recorded yet.</div> : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {revs.list.map(v => (
                  <div key={v.id} style={{ border:'1px solid var(--border,#e3e8f2)', borderRadius:8, padding:'10px 12px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                      <span className="badge badge-normal" style={{ textTransform:'capitalize' }}>{v.action}</span>
                      <span style={{ fontSize:11, color:'var(--text2)' }}>{new Date(v.created_at).toLocaleString()}</span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--text2)' }}>{v.changed_by_name || 'System'}{v.note?` · ${v.note}`:''}</div>
                    <details style={{ marginTop:6 }}>
                      <summary style={{ fontSize:12, cursor:'pointer', color:'var(--primary,#2a5bd7)' }}>View content at this revision</summary>
                      <pre style={{ whiteSpace:'pre-wrap', fontSize:12, marginTop:6, fontFamily:'inherit', color:'var(--text)' }}>{v.body||'(empty)'}</pre>
                    </details>
                  </div>
                ))}
              </div>
            )}
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setRevs(null)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
